import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store/store";
import { webAuth } from "../../services/auth0Api";
import { Auth0Error, Auth0DecodedHash } from 'auth0-js';
import {
  useAuth0LoginBridgeMutation,
  useUpdateStagesMutation,
} from "../../services/api";
import { setUserLogin } from "../../store/reducers/loginReducer";
import { setStage } from "../../store/reducers/stageReducer";
import { setUserFolderId } from "../../store/reducers/usersReducer";
import { setPermission } from "../../store/reducers/permissionReducer";
import { toggleSubmission } from "../../store/reducers/tileReducer";
import useStep from "../../hooks/useStep";
import { loginJourneyTimer, tracker } from "../../services/mixpanel";
import AuthSplitLayout from "../../components/AuthSplitLayout";
import CallbackLoadingState from "./CallbackLoadingState";
import CallbackErrorState from "./CallbackErrorState";
import CallbackSuccessState from "./CallbackSuccessState";

const AuthCallbackComponent = () => {
  const [user, setUser] = useState<Auth0IdTokenPayload | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [systemError, setSystemError] = useState<string | null>(null);

  const [auth0LoginBridge] = useAuth0LoginBridgeMutation();
  const [updateStage] = useUpdateStagesMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setStepFunc } = useStep();
  const isProcessingRef = useRef(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const searchParams = new URLSearchParams(window.location.search);
  const urlErrorDescription = searchParams.get("error_description");

  const [authResponse, setAuthResponse] = useState<Auth0LoginResponse | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleOption | null>(null);
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<RoleOption | null>(null);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [userOptions, setUserOptions] = useState<RoleOption[]>([]);
  const [isProcessingAssigned, setIsProcessingAssigned] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const loadingMessages = [
    "Verifying your identity...",
    "Fetching your account...",
    "Almost there...",
  ];

  const getAccessTokenSilently = useCallback(async () => {
    return localStorage.getItem("auth0Token") || "";
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const hashString = window.location.hash.substring(1);
      const urlParams = new URLSearchParams(hashString);
      const urlState = urlParams.get("state");

      webAuth.parseHash(
        { 
          hash: window.location.hash,
          state: urlState || undefined 
        }, 
        (err: Auth0Error | null, authResult: Auth0DecodedHash | null) => {
          if (err) {
            setSystemError(err.errorDescription || err.description || err.error || "Authentication failed");
            setIsLoading(false);
            return;
          }
          if (authResult && authResult.accessToken && authResult.idTokenPayload) {
            localStorage.setItem("auth0Token", authResult.accessToken);
            localStorage.setItem("idToken", authResult.idToken || "");
            setUser(authResult.idTokenPayload as Auth0IdTokenPayload);
            setIsAuthenticated(true);
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
          }
          setIsLoading(false);
        }
      );
    } else {
      const token = localStorage.getItem("auth0Token");
      if (!token) {
        setIsLoading(false);
        navigate("/login");
      } else {
        setIsLoading(false); 
      }
    }
  }, [navigate]);

  const auth0UserId = user?.['https://purl.com/primary_user_id'] || user?.sub;
  const executeLoginData = useCallback(
    async (data: Auth0LoginResponse) => {
      const {
        user: localUser,
        token: localToken,
        solarStudyId,
        stageData,
        driveData,
        purlUser,
      } = data;
      if (!localUser) return;
      const drive = driveData;
      const folderId = drive?.purlDriveLink ?? drive?.GDriveID;
      if (folderId) {
        localStorage.setItem("folderId", folderId);
        dispatch(setUserFolderId(folderId));
      }

      const userWithContactGroupAndEmail = {
        ...(localUser || {}),
        Contact_Group: purlUser?.Contact_Group,
        email: purlUser?.Email,
      };

      const stage = stageData;
      const newStages = {
        videoComplete: stage.videoComplete,
        purlLoginComplete: stage.purlLoginComplete,
        purlInviteSent: stage.purlInviteSent,
        presentationComplete: stage.presentationComplete,
        benefitCalculatorComplete: stage.benefitCalculatorComplete,
        cpaCallComplete: stage.cpaCallComplete,
        softCommitComplete: stage.softCommitComplete,
        entityInformationCollected: stage.entityInformationCollected,
        purlNdaChecked: stage.purlNdaChecked,
        zohoCustomerId: stage.zohoCustomerId,
        billOfSaleSignedDate: stage.billOfSaleSignedDate ?? null,
        zohoTrancheId: stage.zohoTrancheId ?? null,
      };
      dispatch(setStage(newStages));
      localStorage.setItem("token", localToken);
      localStorage.setItem(
        "user",
        JSON.stringify(userWithContactGroupAndEmail),
      );
      dispatch(setUserLogin(userWithContactGroupAndEmail));

      const newStateData = { ...stageData, purlLoginComplete: true };
      await updateStage({
        zohoId: stage.zohoCustomerId,
        key: "purlLoginComplete",
      });
      localStorage.setItem("newStateData", JSON.stringify(newStateData));
      dispatch(setStage(newStateData));

      if (stage.cpaCallComplete) {
        localStorage.setItem("tile2", JSON.stringify("tile2"));
        dispatch(toggleSubmission({ component: "tile2" }));
      }
      if (stage.softCommitComplete) {
        localStorage.setItem("tile5", JSON.stringify("tile5"));
        dispatch(toggleSubmission({ component: "tile5" }));
      }

      if (solarStudyId) {
        localStorage.setItem("solarId", solarStudyId);
      }

      if (purlUser) {
        const permission = {
          contactGroup: purlUser?.Contact_Group || "OTHER",
        };
        localStorage.setItem("permission", JSON.stringify(permission));
        dispatch(setPermission(permission));
      }

      if (user?.email){
        tracker.passwordMigration.completed(user.email);
        loginJourneyTimer.endAndTrack(user.email);
        tracker.login.success(user.email, selectedRole ? `callback-as-${selectedRole.value}` : "callback");
      } 
      const uniqueId = purlUser.Unique_ID;
      navigate(`/onboarding/${uniqueId}`, { replace: true });
    },
    [
      dispatch,
      navigate,
      setStepFunc,
      updateStage,
      user?.email,
      selectedRole,
    ],
  );

  useEffect(() => {
    const processAuth0Login = async () => {
      if (isLoading || systemError) return;

      if (isAuthenticated && user?.email) {
        if (!auth0UserId) {
          setAuthError("Authentication failed: Missing valid user identification.");
          return;
        }
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
          const auth0Token = await getAccessTokenSilently();
          localStorage.setItem("auth0Token", auth0Token);

          const response = await auth0LoginBridge({
            email: user.email,
            auth0Token: auth0Token,
            auth0UserId: auth0UserId,
          }).unwrap();

          if (response?.data) {
            const options: RoleOption[] = [];
            if (
              response.data.assignedAsCpa &&
              response.data.assignedAsCpa.length > 0
            )
              options.push({ label: "CPA", value: "cpa" });
            if (
              response.data.assignedAsAdvisor &&
              response.data.assignedAsAdvisor.length > 0
            )
              options.push({ label: "Advisor", value: "advisor" });
            if (
              response.data.assignedAsBookkeeper &&
              response.data.assignedAsBookkeeper.length > 0
            )
              options.push({ label: "Bookkeeper", value: "bookkeeper" });
            if (
              response.data.assignedAsSpouse &&
              response.data.assignedAsSpouse.length > 0
            )
              options.push({ label: "Spouse", value: "spouse" });

            if (options.length === 0 && response.data.user) {
              await executeLoginData(response.data);
            } else {
              setRoleOptions(options);
              setAuthResponse(response.data);
            }
          }
        } catch (err: any) {
          const errorMessage =
            err?.data?.data?.message ||
            "Authentication failed. Please try again.";
          console.error("Backend Bridge Failed:", errorMessage);
          setAuthError(errorMessage);
        }
      } else if (!isAuthenticated && !isLoading) {
        navigate("/login");
      }
    };

    processAuth0Login();
  }, [
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    auth0LoginBridge,
    navigate,
    systemError,
    executeLoginData,
    auth0UserId
  ]);

  useEffect(() => {
    if (selectedRole && authResponse) {
      let users: AssignedUser[] = [];
      if (selectedRole.value === "cpa")
        users = authResponse.assignedAsCpa || [];
      else if (selectedRole.value === "advisor")
        users = authResponse.assignedAsAdvisor || [];
      else if (selectedRole.value === "bookkeeper")
        users = authResponse.assignedAsBookkeeper || [];
      else if (selectedRole.value === "spouse")
        users = authResponse.assignedAsSpouse || [];

      setUserOptions(
        users.map((u: AssignedUser) => ({ label: u.email, value: u.email })),
      );
      setSelectedAssignedUser(null);
    } else {
      setUserOptions([]);
    }
  }, [selectedRole, authResponse]);

  useEffect(() => {
    if (authResponse) return;
    const stepInterval = setInterval(() => {
      setLoadingStep((prev) => Math.min(prev + 1, loadingMessages.length - 1));
    }, 1000);
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => Math.min(prev + 1, 88));
    }, 32);
    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, [authResponse]);

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleLoginAsSelf = async () => {
    if (authResponse && authResponse.user) {
      await executeLoginData(authResponse);
    }
  };

  const handleLoginAsAssigned = async () => {
    if (!selectedRole || !selectedAssignedUser) return;
    if (!auth0UserId) {
      setAuthError("Authentication failed: Missing valid user identification.");
      return;
    }
    setIsProcessingAssigned(true);
    try {
      const auth0Token =
        localStorage.getItem("auth0Token") || (await getAccessTokenSilently());
      const response = await auth0LoginBridge({
        email: selectedAssignedUser.value,
        auth0Token: auth0Token,
        auth0UserId: auth0UserId,
        role: selectedRole.value,
      }).unwrap();

      if (response?.data && response.data.user) {
        tracker.login.loginAsAssigned(user?.email || "", selectedRole.value, selectedAssignedUser.value);
        await executeLoginData(response.data);
      } else {
        setAuthError("Failed to authenticate as assigned user.");
      }
    } catch (err: any) {
      const errorMessage =
        err?.data?.data?.message || "Failed to login as assigned user.";
      setAuthError(errorMessage);
    } finally {
      setIsProcessingAssigned(false);
    }
  };

  let displayError = urlErrorDescription || authError || systemError;
  if(displayError === "invitation not found or already used"){
    displayError = "This invitation has already been used or not found.";
  }
  
  useEffect(() => {
    if (displayError) {
      tracker.passwordMigration.failed(displayError);
      tracker.login.failed(user?.email || "", "callback", displayError);
    }
  }, [displayError, user?.email]);

  if (authResponse && !displayError) {
    return (
      <CallbackSuccessState 
        authResponse={authResponse}
        userEmail={user?.email}
        roleOptions={roleOptions}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        userOptions={userOptions}
        selectedAssignedUser={selectedAssignedUser}
        setSelectedAssignedUser={setSelectedAssignedUser}
        isProcessingAssigned={isProcessingAssigned}
        onLoginAsSelf={handleLoginAsSelf}
        onLoginAsAssigned={handleLoginAsAssigned}
      />
    );
  }

  return (
    <AuthSplitLayout subtitle="Securely connecting to your account">
      
      {/* Loading State */}
      {!authResponse && !displayError && (
        <CallbackLoadingState 
          loadingStep={loadingStep}
          loadingProgress={loadingProgress}
          loadingMessages={loadingMessages}
        />
      )}

      {/* Error State */}
      {displayError && (
        <CallbackErrorState 
          error={displayError}
          onBackToLogin={handleBackToLogin}
        />
      )}

    </AuthSplitLayout>
  );
};

export default AuthCallbackComponent;
